import { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch } from "react-redux";
import Chart from "react-apexcharts";
import { fetchLabWiseUtilization, fetchAllLabWiseUtilization } from "../../../API_Service/Dashboard_Slice";
import { companyID } from "../../../Common/UIComponents";
import { useDashboardSignalR } from "../../../Common/useDashboardSignalR";

// Picks a "nice" round step (1/2/5/10 x a power of 10) so the y-axis shows
// a handful of large, evenly-spaced ticks instead of many small ones.
const getNiceYAxisConfig = (maxValue, targetTicks = 4) => {
  if (!maxValue || maxValue <= 0) {
    return { min: 0, max: 10, tickAmount: 5 };
  }

  const rawStep = maxValue / targetTicks;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const residual = rawStep / magnitude;

  let niceStep;
  if (residual <= 1) niceStep = 1 * magnitude;
  else if (residual <= 2) niceStep = 2 * magnitude;
  else if (residual <= 5) niceStep = 5 * magnitude;
  else niceStep = 10 * magnitude;

  const niceMax = Math.ceil(maxValue / niceStep) * niceStep;

  return { min: 0, max: niceMax, tickAmount: Math.round(niceMax / niceStep) };
};

const LabWiseProject = ({ selectedFacilityID = 0, onFacilitySelected, onFacilityListLoaded, onClearSelection }) => {
  const dispatch = useDispatch();
  const [series, setSeries] = useState([]);
  const [options, setOptions] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEmpty, setIsEmpty] = useState(false);
  const chartRef = useRef(null);
  const facilityDataRef = useRef([]);

  const [showAllModal, setShowAllModal] = useState(false);
  const [allLoading, setAllLoading] = useState(false);
  const [allError, setAllError] = useState(null);
  const [allSeries, setAllSeries] = useState([]);
  const [allOptions, setAllOptions] = useState(null);
  const [allChartHeight, setAllChartHeight] = useState(400);
  const [allFacilityData, setAllFacilityData] = useState([]);

  const loadLabWiseUtilization = useCallback(() => {
    if (!companyID) {
      setError("Company ID not found");
      setIsLoading(false);
      return;
    }

    console.log("📊 Loading Lab-wise Utilization - CompanyID:", companyID);
    setIsLoading(true);
    setError(null);
    setIsEmpty(false);

    // Clear existing data
    setSeries([]);
    setOptions(null);

    dispatch(fetchLabWiseUtilization({ companyID, facilityID: 0 }))
      .then((result) => {
        console.log("🔍 Lab-wise Utilization Result:", result);

        let data = result.payload;

        if (result.payload && result.payload.data && Array.isArray(result.payload.data)) {
          data = result.payload.data;
        } else if (Array.isArray(result.payload)) {
          data = result.payload;
        }

        if (!Array.isArray(data) || data.length === 0) {
          setIsEmpty(true);
          setIsLoading(false);
          return;
        }

        // Extract facility names and hours
        const facilityNames = data.map((f) => f.FacilityName || "Unknown");
        const billableHours = data.map((f) => parseFloat(f.BillableHours) || 0);
        const nonBillableHours = data.map((f) => parseFloat(f.NonBillableHours) || 0);

        console.log("📋 Facilities:", facilityNames);
        console.log("💰 Billable Hours:", billableHours);
        console.log("⏱️ Non-Billable Hours:", nonBillableHours);

        // Store facility data in ref for click handler
        facilityDataRef.current = data;
        console.log("💾 Stored facility data in ref. Count:", data.length);

        // Pass facility list to parent for Lab-wise Test Type Analysis dropdown
        if (onFacilityListLoaded) {
          const facilityListData = data.map((f) => ({
            FacilityID: f.FacilityID,
            FacilityName: f.FacilityName,
          }));
          onFacilityListLoaded(facilityListData);
          console.log("📋 Facility List Sent to Parent:", facilityListData);
        }

        // Calculate total hours for each lab
        const totalHours = billableHours.map((b, i) => b + nonBillableHours[i]);

        // Set chart series
        setSeries([
          {
            name: "Billable Hours",
            data: billableHours,
          },
          {
            name: "Non-Billable Hours",
            data: nonBillableHours,
          },
        ]);

        // Store total hours for later use in annotations or custom rendering
        console.log("💰 Total Hours per Lab:", totalHours);

        // Calculate total hours for annotations
        const totalHoursPerLab = billableHours.map((b, i) => b + nonBillableHours[i]);
        const yAxisConfig = getNiceYAxisConfig(Math.max(...totalHoursPerLab, 0));

        // Create event handler for bar clicks
        const handleBarClick = (event, chartContext, config) => {
          console.log("🖱️ ApexCharts Click Event Triggered!");
          console.log("📋 Config:", config);
          console.log("📊 dataPointIndex:", config?.dataPointIndex);
          console.log("📈 seriesIndex:", config?.seriesIndex);

          if (config?.dataPointIndex !== undefined && config.dataPointIndex >= 0) {
            const dataPointIndex = config.dataPointIndex;
            if (data && data[dataPointIndex]) {
              const selectedFacility = data[dataPointIndex];
              const facilityID = selectedFacility.FacilityID || 0;
              const facilityName = selectedFacility.FacilityName || "Unknown";

              console.log("✅ Facility Clicked:", facilityName, "ID:", facilityID);

              if (onFacilitySelected && typeof onFacilitySelected === "function") {
                console.log("🚀 Calling onFacilitySelected with ID:", facilityID);
                onFacilitySelected(facilityID);
              }
            }
          }
        };

        // Create annotations for total hours on top of bars (plain text only)
        const totalHoursAnnotations = {
          points: facilityNames.map((name, index) => ({
            x: name,
            y: totalHoursPerLab[index],
            marker: { size: 0 },
            label: {
              text: totalHoursPerLab[index].toString(),
              style: {
                fontSize: "13px",
                fontWeight: "bold",
                color: "#1e293b",
                background: "transparent",
                border: "none",
                padding: 0,
              },
              offsetY: -15,
              borderWidth: 0,
            },
          })),
        };

        // Set chart options for VERTICAL stacked bar - MODERN PROFESSIONAL DESIGN
        setOptions({
          chart: {
            type: "bar",
            stacked: true,
            toolbar: {
              show: true,
              tools: {
                download: true,
                selection: true,
                zoom: true,
                zoomin: true,
                zoomout: true,
                pan: true,
                reset: true,
              },
            },
            height: 400,
            animations: {
              enabled: true,
              speed: 800,
              animateGradually: {
                enabled: true,
                delay: 150,
              },
              dynamicAnimation: {
                enabled: true,
                speed: 150,
              },
            },
            background: "transparent",
            events: {
              click: handleBarClick,
              dataPointSelection: handleBarClick,
            },
          },
          colors: ["#3B82F6", "#93C5FD"],
          plotOptions: {
            bar: {
              horizontal: false,
              dataLabels: {
                position: "top",
              },
              columnWidth: "65%",
              borderRadius: 6,
              borderRadiusApplication: "end",
            },
          },
          dataLabels: {
            enabled: true,
            formatter: (value) => {
              if (value === 0 || value === null) return "";
              return value.toString();
            },
            style: {
              fontSize: "12px",
              fontWeight: 700,
              colors: ["#ffffff", "#1e293b"],
            },
            position: "center",
            offsetY: 0,
          },
          xaxis: {
            categories: facilityNames,
            title: {
              text: "Labs",
              style: {
                fontSize: "14px",
                fontWeight: 700,
                color: "#1e293b",
              },
            },
            labels: {
              style: {
                fontSize: "13px",
                fontWeight: 600,
                colors: "#475569",
              },
            },
            axisBorder: {
              show: true,
              color: "#e2e8f0",
              height: 1,
            },
            axisTicks: {
              show: false,
            },
          },
          yaxis: {
            ...yAxisConfig,
            title: {
              text: "Hours",
              style: {
                fontSize: "14px",
                fontWeight: 700,
                color: "#1e293b",
              },
            },
            labels: {
              style: {
                fontSize: "13px",
                fontWeight: 600,
                colors: "#475569",
              },
            },
            axisBorder: {
              show: true,
              color: "#e2e8f0",
              width: 1,
            },
            axisTicks: {
              show: false,
            },
          },
          fill: {
            opacity: 1,
            type: "solid",
          },
          stroke: {
            show: true,
            width: 0,
            colors: ["transparent"],
          },
          grid: {
            borderColor: "#e2e8f0",
            strokeDashArray: 0,
            xaxis: {
              lines: {
                show: false,
              },
            },
            yaxis: {
              lines: {
                show: true,
                opacity: 0.1,
              },
            },
            padding: {
              left: 0,
              right: 10,
              top: 0,
              bottom: 0,
            },
          },
          states: {
            hover: {
              filter: {
                type: "darken",
                value: 0.15,
              },
            },
            active: {
              filter: {
                type: "darken",
                value: 0.15,
              },
            },
          },
          annotations: totalHoursAnnotations,
          tooltip: {
            enabled: true,
            theme: "light",
            style: {
              fontSize: "12px",
            },
            y: {
              formatter: (value) => {
                return value ? value.toFixed(2) + " hrs" : "0 hrs";
              },
              title: {
                formatter: (seriesName) => seriesName + ": ",
              },
            },
            marker: {
              show: true,
            },
            style: {
              padding: "10px 14px",
              borderRadius: "8px",
              boxShadow: "0 10px 25px rgba(0, 0, 0, 0.15)",
            },
          },
        });

        setError(null);
      })
      .catch((err) => {
        console.error("❌ Lab-wise Utilization Error:", err);
        setSeries([]);
        setOptions(null);
        setError(err?.message || "Failed to load lab-wise utilization");
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [companyID, dispatch]);

  useEffect(() => {
    loadLabWiseUtilization();
  }, [loadLabWiseUtilization]);

  const loadAllLabs = useCallback(() => {
    if (!companyID) {
      setAllError("Company ID not found");
      return;
    }

    setAllLoading(true);
    setAllError(null);

    dispatch(fetchAllLabWiseUtilization({ companyID }))
      .then((result) => {
        let data = result.payload;

        if (result.payload && result.payload.data && Array.isArray(result.payload.data)) {
          data = result.payload.data;
        } else if (Array.isArray(result.payload)) {
          data = result.payload;
        }

        if (!Array.isArray(data) || data.length === 0) {
          setAllError("No lab utilization data available");
          setAllLoading(false);
          return;
        }

        const facilityNames = data.map((f) => f.FacilityName || f.facilityName || "Unknown");
        const billableHours = data.map((f) => parseFloat(f.BillableHours ?? f.billableHours) || 0);
        const nonBillableHours = data.map((f) => parseFloat(f.NonBillableHours ?? f.nonBillableHours) || 0);
        const totalHoursPerLab = billableHours.map((b, i) => b + nonBillableHours[i]);
        const allYAxisConfig = getNiceYAxisConfig(Math.max(...totalHoursPerLab, 0));

        setAllFacilityData(data);

        setAllSeries([
          { name: "Billable Hours", data: billableHours },
          { name: "Non-Billable Hours", data: nonBillableHours },
        ]);

        const calculatedHeight = Math.max(400, 45 * facilityNames.length);
        setAllChartHeight(calculatedHeight);

        const totalHoursAnnotations = {
          points: facilityNames.map((name, index) => ({
            x: name,
            y: totalHoursPerLab[index],
            marker: { size: 0 },
            label: {
              text: totalHoursPerLab[index].toString(),
              style: {
                fontSize: "13px",
                fontWeight: "bold",
                color: "#1e293b",
                background: "transparent",
                border: "none",
                padding: 0,
              },
              offsetY: -15,
              borderWidth: 0,
            },
          })),
        };

        setAllOptions({
          chart: {
            type: "bar",
            stacked: true,
            toolbar: {
              show: true,
              tools: { download: true },
            },
            animations: { enabled: true, speed: 400 },
            background: "transparent",
          },
          colors: ["#3B82F6", "#93C5FD"],
          plotOptions: {
            bar: {
              horizontal: false,
              dataLabels: { position: "top" },
              columnWidth: facilityNames.length > 15 ? "80%" : "65%",
              borderRadius: 6,
              borderRadiusApplication: "end",
            },
          },
          dataLabels: {
            enabled: true,
            formatter: (value) => (value === 0 || value === null ? "" : value.toString()),
            style: {
              fontSize: "11px",
              fontWeight: 700,
              colors: ["#ffffff", "#1e293b"],
            },
            position: "center",
            offsetY: 0,
          },
          xaxis: {
            categories: facilityNames,
            title: {
              text: "Labs",
              style: { fontSize: "13px", fontWeight: 700, color: "#1e293b" },
            },
            labels: {
              style: { fontSize: "11px", fontWeight: 600, colors: "#475569" },
              rotate: facilityNames.length > 10 ? -45 : 0,
            },
            axisBorder: { show: true, color: "#e2e8f0", height: 1 },
            axisTicks: { show: false },
          },
          yaxis: {
            ...allYAxisConfig,
            title: {
              text: "Hours",
              style: { fontSize: "13px", fontWeight: 700, color: "#1e293b" },
            },
            labels: {
              style: { fontSize: "12px", fontWeight: 600, colors: "#475569" },
            },
            axisBorder: { show: true, color: "#e2e8f0", width: 1 },
            axisTicks: { show: false },
          },
          fill: { opacity: 1, type: "solid" },
          stroke: { show: true, width: 0, colors: ["transparent"] },
          grid: {
            borderColor: "#e2e8f0",
            xaxis: { lines: { show: false } },
            yaxis: { lines: { show: true, opacity: 0.1 } },
          },
          annotations: totalHoursAnnotations,
          tooltip: {
            enabled: true,
            theme: "light",
            style: { fontSize: "12px" },
            y: {
              formatter: (value) => (value ? value.toFixed(2) + " hrs" : "0 hrs"),
              title: { formatter: (seriesName) => seriesName + ": " },
            },
          },
        });

        setAllError(null);
      })
      .catch((err) => {
        console.error("All Lab-wise Utilization Error:", err);
        setAllError(err?.message || "Failed to load all lab-wise utilization");
      })
      .finally(() => {
        setAllLoading(false);
      });
  }, [dispatch]);

  const handleOpenAllLabsModal = () => {
    setShowAllModal(true);
    loadAllLabs();
  };

  // Add custom click handler for VERTICAL bars (LabWiseProject)
  useEffect(() => {
    const chartElement = chartRef.current?.querySelector(".apexcharts-canvas");
    if (!chartElement) return;

    const handleDirectClick = (e) => {
      console.log("\n🖱️ ====== LAB-WISE VERTICAL BAR CLICK ======");

      const currentData = facilityDataRef.current;
      console.log("📦 Facility data length:", currentData?.length);

      if (!currentData || currentData.length === 0) {
        console.log("⚠️ No facility data");
        return;
      }

      // Find the bar rect element
      let barElement = e.target.closest("rect");
      if (!barElement) {
        console.log("⚠️ No rect element found");
        return;
      }

      const svg = chartRef.current?.querySelector("svg");
      if (!svg) return;

      // Get ALL vertical bar rectangles (serie 0 - billable hours)
      const allBarRects = Array.from(svg.querySelectorAll("rect.apexcharts-bar-serie0"));
      console.log("📊 Found", allBarRects.length, "vertical bars");

      if (allBarRects.length === 0) return;

      // Get bounding rectangles and sort by X position (left to right) for VERTICAL bars
      const barsWithBounds = allBarRects.map((rect, idx) => ({
        rect,
        index: idx,
        bounds: rect.getBoundingClientRect(),
      }));

      barsWithBounds.sort((a, b) => a.bounds.left - b.bounds.left);

      console.log("📊 Bars sorted by X position (left to right):");
      barsWithBounds.forEach((item, i) => {
        console.log(`  [${i}] X: ${item.bounds.left.toFixed(0)}, Width: ${item.bounds.width.toFixed(0)}`);
      });

      // Find which bar contains the click point (using X position for vertical bars)
      let dataPointIndex = null;
      const clickX = e.clientX;

      for (let i = 0; i < barsWithBounds.length; i++) {
        const item = barsWithBounds[i];
        if (clickX >= item.bounds.left && clickX <= item.bounds.right) {
          dataPointIndex = i;
          console.log("✅ Click is INSIDE bar at index", i);
          break;
        }
      }

      // If not inside any bar, find the closest one
      if (dataPointIndex === null) {
        console.log("⚠️ Click not inside any bar, finding closest...");
        let closestIdx = 0;
        let closestDistance = Infinity;

        for (let i = 0; i < barsWithBounds.length; i++) {
          const item = barsWithBounds[i];
          const barCenter = item.bounds.left + item.bounds.width / 2;
          const distance = Math.abs(clickX - barCenter);

          console.log(`  [${i}] Distance: ${distance.toFixed(0)}`);

          if (distance < closestDistance) {
            closestDistance = distance;
            closestIdx = i;
          }
        }

        dataPointIndex = closestIdx;
        console.log("📍 Closest bar is at index:", dataPointIndex);
      }

      // Validate and send
      console.log("🎯 Final index:", dataPointIndex, "Valid range: 0 to", currentData.length - 1);

      if (dataPointIndex !== null && dataPointIndex >= 0 && dataPointIndex < currentData.length) {
        const selectedFacility = currentData[dataPointIndex];
        const facilityID = selectedFacility.FacilityID || 0;
        const facilityName = selectedFacility.FacilityName || "Unknown";

        console.log("✨ Selected Facility:");
        console.log("  Name:", facilityName);
        console.log("  ID:", facilityID);

        if (onFacilitySelected && typeof onFacilitySelected === "function") {
          onFacilitySelected(facilityID);
          console.log("✅ Callback executed!");
        }
      } else {
        console.error("❌ ERROR: Invalid index", dataPointIndex);
      }

      console.log("🖱️ ====== END CLICK ======\n");
    };

    chartElement.addEventListener("click", handleDirectClick);
    return () => chartElement.removeEventListener("click", handleDirectClick);
  }, [onFacilitySelected]);

  useDashboardSignalR(loadLabWiseUtilization);

  if (error) {
    return (
      <div className="mb-8 p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-600">Error loading lab-wise utilization: {error}</p>
      </div>
    );
  }

  if (isEmpty) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
        <h3 className="text-lg font-bold text-gray-900 mb-6">
          Lab-wise Utilization
        </h3>
        <div className="text-center text-slate-500 py-12">
          <span className="material-symbols-outlined text-4xl mb-2 block opacity-30">inbox</span>
          <p className="font-medium">No lab utilization data available</p>
        </div>
      </div>
    );
  }

  if (isLoading || !options) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-bold text-gray-900">Lab-wise Project Utilization</h3>
        </div>
        <div className="animate-pulse">
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="bg-gradient-to-br from-white to-slate-50 rounded-2xl border border-slate-200 p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h3 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">
            Lab-wise Project Utilization
          </h3>
          <p className="text-sm text-slate-500 mt-1">Billable vs Non-Billable hours per laboratory</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleOpenAllLabsModal}
            className="px-4 py-2 text-sm font-semibold text-[#08757C] border-2 border-[#08757C]/30 rounded-lg hover:bg-[#08757C]/5 transition-colors"
          >
            View All Labs
          </button>
          {selectedFacilityID > 0 && onClearSelection && (
            <button
              onClick={onClearSelection}
              className="px-3 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg text-sm font-semibold transition-colors flex items-center gap-2"
            >
              <span>✕</span>
              <span>Clear</span>
            </button>
          )}
          <div className="p-3 bg-blue-100 rounded-full">
            <span className="material-symbols-outlined text-blue-600">domain</span>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-6 mb-6 pb-6 border-b border-slate-200">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: "#3B82F6" }}></div>
          <span className="text-sm font-semibold text-slate-700">Billable Hours</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: "#93C5FD" }}></div>
          <span className="text-sm font-semibold text-slate-700">Non-Billable Hours</span>
        </div>
      </div>

      {/* Chart - ApexCharts Events Handle Clicks */}
      {series.length > 0 && options ? (
        <div
          ref={chartRef}
          className="rounded-xl overflow-hidden bg-white p-4 cursor-pointer"
        >
          <Chart
            options={options}
            series={series}
            type="bar"
            height={400}
          />
        </div>
      ) : (
        <div className="text-center text-slate-500 py-12">
          <span className="material-symbols-outlined text-4xl mb-2 block opacity-30">inbox</span>
          <p className="font-medium">No data available</p>
        </div>
      )}
    </div>

    {/* View All Labs Modal */}
    {showAllModal && (
      <div
        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        onClick={() => setShowAllModal(false)}
      >
        <div
          className="bg-white rounded-2xl shadow-2xl w-full max-w-[95vw] max-h-[90vh] overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Modal Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
            <div>
              <h3 className="text-xl font-bold text-slate-900">All Labs - Utilization</h3>
              <p className="text-sm text-slate-500 mt-1">Complete list of all laboratories</p>
            </div>
            <button
              type="button"
              onClick={() => setShowAllModal(false)}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <span className="material-symbols-outlined text-slate-600">close</span>
            </button>
          </div>

          {/* Modal Body */}
          <div className="flex-1 overflow-y-auto p-6">
            {allLoading ? (
              <div className="animate-pulse">
                <div className="h-64 bg-gray-200 rounded"></div>
              </div>
            ) : allError ? (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600">Error: {allError}</p>
              </div>
            ) : allSeries.length > 0 && allOptions ? (
              <>
                {/* Legend */}
                <div className="flex items-center gap-6 mb-6 pb-4 border-b border-slate-200">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: "#3B82F6" }}></div>
                    <span className="text-sm font-semibold text-slate-700">Billable Hours</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: "#93C5FD" }}></div>
                    <span className="text-sm font-semibold text-slate-700">Non-Billable Hours</span>
                  </div>
                  <div className="ml-auto text-sm text-slate-500">
                    Total Labs: <span className="font-bold text-slate-900">{allFacilityData.length}</span>
                  </div>
                </div>

                <Chart
                  options={allOptions}
                  series={allSeries}
                  type="bar"
                  height={allChartHeight}
                />
              </>
            ) : (
              <div className="text-center text-slate-500 py-12">
                <span className="material-symbols-outlined text-4xl mb-2 block opacity-30">inbox</span>
                <p className="font-medium">No data available</p>
              </div>
            )}
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default LabWiseProject;
